import { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import AdminLayout from '@/components/AdminLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import axios from 'axios';
import { Plus, Package, Pencil, Trash2, DollarSign, Box, Barcode, Tag, Store, RotateCcw } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const iconOptions = [
  { value: 'package', label: 'Paquete' },
  { value: 'droplet', label: 'Gota' },
];

export default function AdminProducts() {
  const { user, getAuthHeaders } = useAuth();
  const [products, setProducts] = useState([]);
  const [branches, setBranches] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState('base');
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    price: '',
    cost: '',
    stock: '0',
    barcode: '',
    icon: 'package',
    is_neurone: false,
  });

  const isMasterAdmin = user?.role === 'master_admin';

  useEffect(() => {
    fetchBranches();
    fetchProducts();
  }, []);

  useEffect(() => {
    fetchProducts();
  }, [selectedBranch]);

  const fetchBranches = async () => {
    try {
      const response = await axios.get(`${API}/branches`, getAuthHeaders());
      setBranches(response.data);
    } catch (error) {
      // error handled by toast
    }
  };

  const fetchProducts = async () => {
    setLoading(true);
    try {
      if (selectedBranch === 'base') {
        const response = await axios.get(`${API}/products`, getAuthHeaders());
        setProducts(response.data.map(p => ({ ...p, effective_price: p.price, has_branch_price: false })));
      } else {
        const response = await axios.get(
          `${API}/branch-prices/by-branch/${selectedBranch}?item_type=product`,
          getAuthHeaders()
        );
        setProducts(response.data.items);
      }
    } catch (error) {
      toast.error('Error al cargar productos');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const payload = {
      ...formData,
      price: parseFloat(formData.price),
      cost: parseFloat(formData.cost) || 0,
      stock: parseInt(formData.stock),
      barcode: formData.barcode || null,
      is_neurone: !!formData.is_neurone,
    };
    
    try {
      if (editingProduct) {
        if (selectedBranch === 'base') {
          await axios.put(`${API}/products/${editingProduct.id}`, payload, getAuthHeaders());
          toast.success('Producto actualizado');
        } else {
          await axios.post(`${API}/branch-prices`, {
            item_id: editingProduct.id,
            item_type: 'product',
            branch_id: selectedBranch,
            price: parseFloat(formData.price)
          }, getAuthHeaders());
          toast.success('Precio de sucursal actualizado');
        }
      } else {
        await axios.post(`${API}/products`, payload, getAuthHeaders());
        toast.success('Producto creado');
      }
      setIsDialogOpen(false);
      resetForm();
      fetchProducts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar');
    }
  };

  const resetForm = () => {
    setFormData({ name: '', price: '', cost: '', stock: '0', barcode: '', icon: 'package', is_neurone: false });
    setEditingProduct(null);
  };

  const handleEdit = (product) => {
    setEditingProduct(product);
    setFormData({
      name: product.name,
      price: (product.branch_price || product.effective_price || product.price).toString(),
      cost: (product.cost || 0).toString(),
      stock: product.stock.toString(),
      barcode: product.barcode || '',
      icon: product.icon,
      is_neurone: !!product.is_neurone,
    });
    setIsDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar este producto?')) return;
    try {
      await axios.delete(`${API}/products/${id}`, getAuthHeaders());
      toast.success('Producto eliminado');
      fetchProducts();
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  const handleResetBranchPrice = async (product) => {
    if (!window.confirm(`¿Restaurar precio base para "${product.name}" en esta sucursal?`)) return;
    try {
      const pricesRes = await axios.get(
        `${API}/branch-prices?branch_id=${selectedBranch}&item_type=product`,
        getAuthHeaders()
      );
      const branchPrice = pricesRes.data.find(p => p.item_id === product.id);
      if (branchPrice) {
        await axios.delete(`${API}/branch-prices/${branchPrice.id}`, getAuthHeaders());
        toast.success('Precio restaurado al base');
        fetchProducts();
      }
    } catch (error) {
      toast.error('Error al restaurar precio');
    }
  };

  const getMargin = (price, cost) => {
    if (!cost || cost === 0) return 0;
    return ((price - cost) / cost * 100).toFixed(0);
  };

  const getBranchName = () => {
    if (selectedBranch === 'base') return null;
    return branches.find(b => b.id === selectedBranch)?.name || '';
  };

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="admin-products">
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <h1 className="font-heading text-3xl text-white tracking-tight">Productos</h1>
            <p className="text-zinc-500 mt-1">
              {selectedBranch === 'base' 
                ? 'Precios base para todas las sucursales'
                : `Precios para ${getBranchName()}`}
            </p>
          </div>
          
          <div className="flex items-center gap-3">
            {/* Branch Selector */}
            {isMasterAdmin && (
              <div className="flex items-center gap-2">
                <Store className="w-4 h-4 text-zinc-500" />
                <Select value={selectedBranch} onValueChange={setSelectedBranch}>
                  <SelectTrigger className="w-[220px] bg-zinc-800 border-zinc-700 text-white">
                    <SelectValue placeholder="Seleccionar sucursal" />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-800 border-zinc-700">
                    <SelectItem value="base" className="text-amber-500">
                      💰 Precios Base (Global)
                    </SelectItem>
                    {branches.map((branch) => (
                      <SelectItem key={branch.id} value={branch.id} className="text-white">
                        {branch.business_name?.split(' ')[0]} - {branch.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {selectedBranch === 'base' && (
              <Dialog open={isDialogOpen} onOpenChange={(open) => { setIsDialogOpen(open); if (!open) resetForm(); }}>
                <DialogTrigger asChild>
                  <Button 
                    className="bg-gold text-black hover:bg-gold-dark"
                    data-testid="add-product-btn"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Nuevo Producto
                  </Button>
                </DialogTrigger>
                <DialogContent className="bg-zinc-900 border-zinc-800 max-w-md">
                  <DialogHeader>
                    <DialogTitle className="font-heading text-xl text-white">
                      Nuevo Producto
                    </DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                    <div className="space-y-2">
                      <Label className="text-zinc-400">Nombre</Label>
                      <Input
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        placeholder="Cera para Cabello"
                        className="bg-zinc-800 border-zinc-700 text-white"
                        data-testid="product-name-input"
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-zinc-400">Código de Barras</Label>
                      <Input
                        value={formData.barcode}
                        onChange={(e) => setFormData({ ...formData, barcode: e.target.value.toUpperCase() })}
                        placeholder="CERA001 (se genera automáticamente si está vacío)"
                        className="bg-zinc-800 border-zinc-700 text-white font-mono"
                        data-testid="product-barcode-input"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label className="text-zinc-400">Costo ($)</Label>
                        <Input
                          type="number"
                          step="0.01"
                          value={formData.cost}
                          onChange={(e) => setFormData({ ...formData, cost: e.target.value })}
                          placeholder="90.00"
                          className="bg-zinc-800 border-zinc-700 text-white"
                          data-testid="product-cost-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-zinc-400">Precio de Venta ($)</Label>
                        <Input
                          type="number"
                          step="0.01"
                          value={formData.price}
                          onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                          placeholder="180.00"
                          className="bg-zinc-800 border-zinc-700 text-white"
                          data-testid="product-price-input"
                          required
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label className="text-zinc-400">Stock Inicial</Label>
                        <Input
                          type="number"
                          value={formData.stock}
                          onChange={(e) => setFormData({ ...formData, stock: e.target.value })}
                          placeholder="0"
                          className="bg-zinc-800 border-zinc-700 text-white"
                          data-testid="product-stock-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-zinc-400">Icono</Label>
                        <Select
                          value={formData.icon}
                          onValueChange={(value) => setFormData({ ...formData, icon: value })}
                        >
                          <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="bg-zinc-800 border-zinc-700">
                            {iconOptions.map((opt) => (
                              <SelectItem key={opt.value} value={opt.value} className="text-white">
                                {opt.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    {formData.cost && formData.price && (
                      <div className="p-3 bg-zinc-800 rounded-sm">
                        <p className="text-xs text-zinc-500">Margen de ganancia</p>
                        <p className="text-gold font-semibold">
                          {getMargin(parseFloat(formData.price), parseFloat(formData.cost))}%
                        </p>
                      </div>
                    )}

                    {/* is_neurone flag */}
                    <label className="flex items-center gap-3 p-3 rounded-sm bg-zinc-800/50 border border-zinc-700 cursor-pointer hover:border-purple-500/50 transition-colors" data-testid="is-neurone-label">
                      <input
                        type="checkbox"
                        checked={!!formData.is_neurone}
                        onChange={(e) => setFormData({ ...formData, is_neurone: e.target.checked })}
                        className="w-4 h-4 rounded bg-zinc-900 border-zinc-600 text-purple-500 focus:ring-purple-500"
                        data-testid="product-is-neurone-checkbox"
                      />
                      <div className="flex-1">
                        <span className="text-sm text-white font-medium">Marcar como Neurone</span>
                        <p className="text-[10px] text-zinc-500">Productos Neurone se filtran aparte en metas y reportes</p>
                      </div>
                      {formData.is_neurone && (
                        <span className="text-[10px] px-2 py-0.5 rounded bg-purple-500/20 text-purple-400 font-semibold uppercase tracking-wider">Neurone</span>
                      )}
                    </label>

                    <div className="flex gap-3 pt-4">
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => setIsDialogOpen(false)}
                        className="flex-1 border-zinc-700 text-zinc-300 hover:bg-zinc-800"
                      >
                        Cancelar
                      </Button>
                      <Button
                        type="submit"
                        className="flex-1 bg-gold text-black hover:bg-gold-dark"
                        data-testid="product-submit-btn"
                      >
                        Crear
                      </Button>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>
            )}
          </div>
        </div>

        {/* Info Banner */}
        {selectedBranch !== 'base' && isMasterAdmin && (
          <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
            <p className="text-blue-400 text-sm">
              💡 Editando precios para <strong>{getBranchName()}</strong>. 
              Los precios que modifiques aquí solo aplicarán a esta sucursal. 
              Los productos sin precio personalizado usarán el precio base.
            </p>
          </div>
        )}

        {/* Products Grid */}
        {loading ? (
          <div className="text-center py-12 text-zinc-500">Cargando...</div>
        ) : products.length === 0 ? (
          <Card className="bg-zinc-900/50 border-zinc-800 p-12 text-center">
            <Package className="w-12 h-12 text-zinc-700 mx-auto mb-4" />
            <p className="text-zinc-500">No hay productos registrados</p>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {products.map((product) => (
              <Card 
                key={product.id} 
                className={`bg-zinc-900/50 border-zinc-800 p-5 hover:border-zinc-700 transition-colors ${
                  product.has_branch_price ? 'ring-1 ring-blue-500/50' : ''
                }`}
                data-testid={`product-card-${product.id}`}
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="w-10 h-10 rounded-sm bg-gold/10 flex items-center justify-center">
                    <Package className="w-5 h-5 text-gold" />
                  </div>
                  <div className="flex gap-1">
                    {isMasterAdmin && (
                      <button
                        onClick={() => handleEdit(product)}
                        className="w-7 h-7 rounded-sm bg-zinc-800 hover:bg-zinc-700 flex items-center justify-center text-zinc-400 hover:text-white transition-colors"
                        title="Editar precio"
                      >
                        <Pencil className="w-3 h-3" />
                      </button>
                    )}
                    {selectedBranch !== 'base' && product.has_branch_price && (
                      <button
                        onClick={() => handleResetBranchPrice(product)}
                        className="w-7 h-7 rounded-sm bg-zinc-800 hover:bg-amber-500/20 flex items-center justify-center text-zinc-400 hover:text-amber-500 transition-colors"
                        title="Restaurar precio base"
                      >
                        <RotateCcw className="w-3 h-3" />
                      </button>
                    )}
                    {selectedBranch === 'base' && (
                      <button
                        onClick={() => handleDelete(product.id)}
                        className="w-7 h-7 rounded-sm bg-zinc-800 hover:bg-red-500/20 flex items-center justify-center text-zinc-400 hover:text-red-500 transition-colors"
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2 mb-1">
                  <h3 className="font-medium text-white">{product.name}</h3>
                  {product.is_neurone && (
                    <span className="text-[9px] px-1.5 py-0.5 rounded bg-purple-500/20 text-purple-400 font-semibold uppercase tracking-wider" data-testid={`product-neurone-badge-${product.id}`}>
                      Neurone
                    </span>
                  )}
                </div>
                <p className="text-xs text-zinc-600 font-mono mb-3 flex items-center gap-1">
                  <Barcode className="w-3 h-3" />
                  {product.barcode}
                </p>
                <div className="space-y-2 text-sm">
                  {selectedBranch === 'base' && (
                    <div className="flex items-center justify-between">
                      <span className="flex items-center gap-1 text-zinc-500">
                        <Tag className="w-3 h-3" />
                        Costo
                      </span>
                      <span className="text-zinc-400">${(product.cost || 0).toFixed(2)}</span>
                    </div>
                  )}
                  <div className="flex items-center justify-between">
                    <span className="flex items-center gap-1 text-zinc-500">
                      <DollarSign className="w-3 h-3" />
                      Precio
                    </span>
                    {product.has_branch_price ? (
                      <div className="text-right">
                        <span className="text-blue-400 font-semibold">${product.effective_price.toFixed(2)}</span>
                        <p className="text-xs text-zinc-600 line-through">Base: ${product.price.toFixed(2)}</p>
                      </div>
                    ) : (
                      <span className="text-gold font-semibold">${(product.effective_price || product.price).toFixed(2)}</span>
                    )}
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="flex items-center gap-1 text-zinc-500">
                      <Box className="w-3 h-3" />
                      Stock
                    </span>
                    <span className={`font-semibold ${product.stock < 10 ? 'text-red-500' : 'text-white'}`}>
                      {product.stock} pzas
                    </span>
                  </div>
                  {selectedBranch === 'base' && product.cost > 0 && (
                    <div className="pt-2 border-t border-zinc-800">
                      <div className="flex items-center justify-between">
                        <span className="text-zinc-500">Margen</span>
                        <span className="text-green-500 font-semibold">
                          {getMargin(product.price, product.cost)}%
                        </span>
                      </div>
                    </div>
                  )}
                </div>
                {product.has_branch_price && (
                  <div className="mt-2 pt-2 border-t border-zinc-800">
                    <span className="text-xs text-blue-400">✓ Precio personalizado</span>
                  </div>
                )}
              </Card>
            ))}
          </div>
        )}

        {/* Edit Price Dialog for Branch */}
        {selectedBranch !== 'base' && editingProduct && (
          <Dialog open={isDialogOpen} onOpenChange={(open) => { setIsDialogOpen(open); if (!open) resetForm(); }}>
            <DialogContent className="bg-zinc-900 border-zinc-800">
              <DialogHeader>
                <DialogTitle className="font-heading text-xl text-white">
                  Editar Precio - {getBranchName()}
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                <div className="p-3 bg-zinc-800 rounded-lg">
                  <p className="text-white font-medium">{editingProduct.name}</p>
                  <p className="text-xs text-zinc-500">Precio base: ${editingProduct.price.toFixed(2)}</p>
                </div>
                <div className="space-y-2">
                  <Label className="text-zinc-400">Precio para esta sucursal ($)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={formData.price}
                    onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                    placeholder="180.00"
                    className="bg-zinc-800 border-zinc-700 text-white text-lg"
                    data-testid="branch-price-input"
                    required
                    autoFocus
                  />
                </div>
                <div className="flex gap-3 pt-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setIsDialogOpen(false)}
                    className="flex-1 border-zinc-700 text-zinc-300 hover:bg-zinc-800"
                  >
                    Cancelar
                  </Button>
                  <Button
                    type="submit"
                    className="flex-1 bg-gold text-black hover:bg-gold-dark"
                  >
                    Guardar Precio
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>
    </AdminLayout>
  );
}
